import { Component, OnInit, SecurityContext } from '@angular/core';
import { User } from '../../../models/User/user';
import { AppContextService } from '../../../services/app-context.service';
import { OperationResult } from '../../../models/server-result/operation-result';
import { ConfirmingContract } from '../../../models/contracts/confirming/confirming-contract';
import { ReportingApiService } from '../../../server-api/reporting-api.service';
import { ServerApiService } from '../../../server-api/server-api.service';
import { ToasterService } from '../../../shared-components/custom-toaster/toaster.service';
import { HttpErrorResponse } from '@angular/common/http';
import { LogService } from '../../../services/log/log.service';
import { DateLocalePipe } from '../../../services/date-locale.pipe';
import { ConfirmingConnectionsSearchCriteria } from '../../../models/contracts/confirming/confirming-connections-search-criteria';
import { ConfirmingConnection } from '../../../models/contracts/confirming/confirming-connection';
import { forkJoin, Observable } from 'rxjs';
import { OperationError } from '../../../models/server-result/operation-error';
import { GetFinancierConfirmingContractsInput } from '../../../models/contracts/confirming/get-financier-confirming-contracts';
import { FactoringContractsSearchCriteria } from '../../../models/contracts/factoring/factoring-contracts-search-criteria';
import { FactoringConnectionsSearchCriteria } from '../../../models/contracts/factoring/factoring-connections-search-criteria';
import { FactoringConnection } from '../../../models/contracts/factoring/factoring-connection';
import { CurrencyCode } from '../../../models/enums/currency-code';
import { DomSanitizer } from '@angular/platform-browser';
import { PendingApprovalDocumentsAmountDto } from '../../../models/dashboard/pending-approval-documents-amount-dto';
import { Router } from '@angular/router';
import { FileIntegrationStateService } from '../../../services/file-integration-state.service';
import { SearchFilesIntegrationComponent } from '../../files-integration/search-files-integration/search-files-integration.component';
import { FileStatus } from '../../../models/enums/FileIntegration/integration-file-status';
import { TranslateService } from '../../../services/translate/translate.service';

type CurrencyViewModel = {
	Currency: string;
	Selected: boolean;
};

@Component({
	selector: 'app-dashboard-financier',
	templateUrl: './dashboard-financier.component.html',
	styleUrls: ['../private-home.component.scss', './dashboard-financier.component.scss'],
	standalone: false,
})
export class DashboardFinancierComponent implements OnInit {
	public loggedUser: User | null = new User();
	public requestErrors: OperationError[] = [];

	public CurrentMonth: Date;

	public registeredBuyerCompanies: number = 0;
	public contractsAvailableAmount: number = 0;
	public contractedAmount: number = 0;
	public approvedRequestFunds: number = 0;
	public contractsAvailableAmountRatio: number = 0;
	public buyersWithActiveContractsCompaniesCount: number = 0;
	public suppliersWithActiveConnectionsCompaniesCount: number = 0;
	public suppliersActiveConnections: number = 0;
	public monthlyAutomaticPayments: number = 0;
	public currentFundingRequestsAmount: number = 0;
	public notPaidDocuments: number = 0;

	private month: number = new Date().getMonth() + 1;
	private year: number = new Date().getFullYear();

	public selectedCurrency: string;
	public orderedCurrencies: CurrencyViewModel[] = [];

	public confirmingTabVisible: boolean = false;
	public factoringTabVisible: boolean = false;
	public confirmingTabSelected: boolean = false;
	public factoringTabSelected: boolean = false;

	public factoringApprovedCreditAssignments: number = 0;
	public factoringCurrentCreditAssignmentsAmount: number = 0;
	public factoringApprovedAdvances: number = 0;
	public factoringContractedAmount: number = 0;
	public factoringContractDocumentPortfolioAmount: number = 0;
	public factoringContractAdvanceableAmount: number = 0;
	public factoringSuppliersWithActiveContractsCompaniesCount: number = 0;
	public factoringBuyersWithActiveConnectionsCompaniesCount: number = 0;
	public factoringBuyersActiveConnections: number = 0;

	public confirmingPendingApprovalDocumentsAmount: number = 0;
	public factoringPendingApprovalDocumentsAmount: number = 0;

	protected CurrencyCode = CurrencyCode;
	protected FileStatus = FileStatus;

	// Loading states for dashboard cards
	public loadingContractedAmount: boolean = false;
	public loadingContractsAvailableAmount: boolean = false;
	public loadingNotPaidDocuments: boolean = false;
	public loadingApprovedRequestFunds: boolean = false;
	public loadingCurrentFundingRequestsAmount: boolean = false;
	public loadingMonthlyAutomaticPayments: boolean = false;
	public loadingBuyersWithActiveContractsCompaniesCount: boolean = false;
	public loadingSuppliersWithActiveConnectionsCompaniesCount: boolean = false;
	public loadingSuppliersActiveConnections: boolean = false;
	public loadingConfirmingPendingApprovalDocumentsAmount: boolean = false;

	public loadingFactoringApprovedCreditAssignments: boolean = false;
	public loadingFactoringCurrentCreditAssignmentsAmount: boolean = false;
	public loadingFactoringApprovedAdvances: boolean = false;
	public loadingFactoringContractedAmount: boolean = false;
	public loadingFactoringContractDocumentPortfolioAmount: boolean = false;
	public loadingFactoringContractAdvanceableAmount: boolean = false;
	public loadingFactoringSuppliersWithActiveContractsCompaniesCount: boolean = false;
	public loadingFactoringBuyersWithActiveConnectionsCompaniesCount: boolean = false;
	public loadingFactoringBuyersActiveConnections: boolean = false;
	public loadingFactoringPendingApprovalDocumentsAmount: boolean = false;

	constructor(
		private reportingApiService: ReportingApiService,
		private serverApiService: ServerApiService,
		private toasterService: ToasterService,
		private logger: LogService,
		private appContext: AppContextService,
		private dateLocale: DateLocalePipe,
		private domSanitizer: DomSanitizer,
		private router: Router,
		private fileIntegrationStateService: FileIntegrationStateService,
		private translateService: TranslateService
	) {}

	ngOnInit() {
		this.appContext.getLoggedUserAsync().subscribe((userResult) => {
			this.loggedUser = userResult.Data;

			this.CurrentMonth = this.dateLocale.transform(new Date(), 'MM/yyyy');

			if (this.loggedUser?.canSeeConfirmingOptions()) {
				this.confirmingTabVisible = true;
				this.confirmingTabSelected = true;
				this.clickedConfirmingTab();
			}

			if (this.loggedUser?.canSeeFactoringOptions()) {
				this.factoringTabVisible = true;
				this.factoringTabSelected = !this.confirmingTabSelected;

				if (this.factoringTabSelected) this.clickedFactoringTab();
			}
		});
	}

	public refreshAllData(): void {
		if (this.confirmingTabSelected) {
			this.getContracts();
		} else if (this.factoringTabSelected) {
			this.getFactoringApprovedCreditAssignments(CurrencyCode.Euro);
			this.getFactoringCurrentCreditAssignmentsAmount(CurrencyCode.Euro);
			this.getFactoringApprovedAdvances(CurrencyCode.Euro);
			this.getFactoringSuppliersWithActiveContractsCompaniesCount();
			this.getFactoringBuyersWithActiveConnectionsCompaniesCount();
			this.getFactoringBuyersActiveConnections();
			this.getPendingApprovalDocumentsAmount(CurrencyCode.Euro);
		}
	}

	private getCurrentFundingRequestsAmount(currency: string): void {
		this.loadingCurrentFundingRequestsAmount = true;
		this.currentFundingRequestsAmount = 0;
		this.reportingApiService.getConfirmingCurrentFundingRequestsAmount(currency).subscribe({
			next: (opResult: OperationResult<number>) => {
				this.loadingCurrentFundingRequestsAmount = false;
				opResult.IsValid
					? (this.currentFundingRequestsAmount = opResult.Data)
					: this.logger.error('[DashboardFinancierComponent] Invalid result from getCurrentFundingRequestsAmount()', opResult);
			},
			error: (httpErrorResponse: HttpErrorResponse) => {
				this.loadingCurrentFundingRequestsAmount = false;
				this.logger.error(
					'[DashboardFinancierComponent] Invalid result from getCurrentFundingRequestsAmount()',
					this.domSanitizer.sanitize(SecurityContext.HTML, httpErrorResponse)
				);
			},
		});
	}

	private getMonthlyAutomaticPayments(currency: string): void {
		this.loadingMonthlyAutomaticPayments = true;
		this.monthlyAutomaticPayments = 0;
		this.reportingApiService.getConfirmingMonthlyAutomaticPayments(this.month, this.year, currency).subscribe({
			next: (opResult: OperationResult<number>) => {
				this.loadingMonthlyAutomaticPayments = false;
				opResult.IsValid
					? (this.monthlyAutomaticPayments = opResult.Data)
					: this.logger.error('[DashboardFinancierComponent] Invalid result from getMonthlyAutomaticPayments()', opResult);
			},
			error: (httpErrorResponse: HttpErrorResponse) => {
				this.loadingMonthlyAutomaticPayments = false;
				this.logger.error(
					'[DashboardFinancierComponent] Invalid result from getMonthlyAutomaticPayments()',
					this.domSanitizer.sanitize(SecurityContext.HTML, httpErrorResponse)
				);
			},
		});
	}

	private getSuppliersActiveConnections(currency: string): void {
		this.loadingSuppliersActiveConnections = true;
		this.suppliersActiveConnections = 0;
		this.reportingApiService.getConfirmingSuppliersActiveConnections(currency).subscribe({
			next: (opResult: OperationResult<number>) => {
				this.loadingSuppliersActiveConnections = false;
				opResult.IsValid
					? (this.suppliersActiveConnections = opResult.Data)
					: this.logger.error('[DashboardFinancierComponent] Invalid result from getSuppliersActiveConnections()', opResult);
			},
			error: (httpErrorResponse: HttpErrorResponse) => {
				this.loadingSuppliersActiveConnections = false;
				this.logger.error(
					'[DashboardFinancierComponent] Invalid result from getSuppliersActiveConnections()',
					this.domSanitizer.sanitize(SecurityContext.HTML, httpErrorResponse)
				);
			},
		});
	}

	private getPendingApprovalDocumentsAmount(currency: string) {
		this.loadingConfirmingPendingApprovalDocumentsAmount = true;
		this.loadingFactoringPendingApprovalDocumentsAmount = true;
		this.confirmingPendingApprovalDocumentsAmount = 0;
		this.factoringPendingApprovalDocumentsAmount = 0;
		this.reportingApiService.getPendingApprovalDocumentsAmount(currency).subscribe({
			next: (opResult: OperationResult<PendingApprovalDocumentsAmountDto>) => {
				this.loadingConfirmingPendingApprovalDocumentsAmount = false;
				this.loadingFactoringPendingApprovalDocumentsAmount = false;
				if (opResult.IsValid) {
					this.confirmingPendingApprovalDocumentsAmount = opResult.Data.Confirming;
					this.factoringPendingApprovalDocumentsAmount = opResult.Data.Factoring;
				} else {
					this.logger.error('[DashboardAdminitratorComponent] Invalid result from getPendingApprovalDocumentsAmount', opResult);
				}
			},
			error: (httpErrorResponse: HttpErrorResponse) => {
				this.loadingConfirmingPendingApprovalDocumentsAmount = false;
				this.loadingFactoringPendingApprovalDocumentsAmount = false;
				this.logger.error(
					'[DashboardAdminitratorComponent] HttpError result from getPendingApprovalDocumentsAmount',
					this.domSanitizer.sanitize(SecurityContext.HTML, httpErrorResponse)
				);
			},
		});
	}

	private getContracts() {
		// Set loading states for values that are calculated in this method
		this.loadingContractedAmount = true;
		this.loadingContractsAvailableAmount = true;
		this.loadingBuyersWithActiveContractsCompaniesCount = true;

		this.resetValues();

		let servicesCallsToMake: Array<Observable<OperationResult<ConfirmingContract[]>>> = [];

		this.loggedUser?.Companies.forEach((company) => {
			servicesCallsToMake.push(
				this.serverApiService.getConfirmingContractsForFinancier(<GetFinancierConfirmingContractsInput>{
					FinancierId: company.Id!,
				})
			);
		});

		const getAllRequests = forkJoin(servicesCallsToMake);

		getAllRequests.subscribe((opResults) => {
			let currencies: CurrencyViewModel[] = [];
			let retrievedConfirmingContracts: ConfirmingContract[] = [];

			for (let index = 0; index < servicesCallsToMake.length; index++) {
				let getConfirmingContracts = opResults[index] as OperationResult<ConfirmingContract[]>;

				if (!getConfirmingContracts.IsValid) {
					let errors = getConfirmingContracts.Errors;
					if (errors && errors.length > 0) {
						this.toasterService.showError(this.translateService.translate('VALIDATION_FAILED', 'list-errorCodes'), {
							errors: errors,
							autoClose: false,
						});
					}
				} else {
					if (!!getConfirmingContracts.Data && getConfirmingContracts.Data.length > 0) {
						this.registeredBuyerCompanies += getConfirmingContracts.Data.length;

						getConfirmingContracts.Data.forEach((contract) => {
							retrievedConfirmingContracts.push(contract);

							if (currencies.findIndex((c) => c.Currency == contract.Currency) == -1)
								currencies.push(<CurrencyViewModel>{
									Currency: contract.Currency,
									Selected: false,
								});
						});
					}
				}
			}

			this.orderedCurrencies = currencies.sort(function (c1: CurrencyViewModel, c2: CurrencyViewModel) {
				var currency1 = c1.Currency.toUpperCase();
				var currency2 = c2.Currency.toUpperCase();
				return currency1 < currency2 ? -1 : currency1 > currency2 ? 1 : 0;
			});

			if (!!this.orderedCurrencies && this.orderedCurrencies.length > 0) {
				let initialCurrencytoProcess = this.orderedCurrencies[0].Currency;
				this.orderedCurrencies[0].Selected = true;

				let contractsOfInitialCurrency: ConfirmingContract[] = retrievedConfirmingContracts.filter(
					(c) => c.Currency == initialCurrencytoProcess
				);

				contractsOfInitialCurrency.forEach((contract) => {
					this.contractedAmount += contract.ContractedAmount;
					this.contractsAvailableAmount += contract.AvailableAmount;
					this.contractsAvailableAmountRatio =
						this.contractedAmount > 0 ? 100.0 * (this.contractsAvailableAmount / this.contractedAmount) : 0;
				});

				this.buyersWithActiveContractsCompaniesCount = contractsOfInitialCurrency
					.filter((contract) => contract.ContractState == 'Active')
					.map((c) => c.Buyer.Id)
					.filter((x, i, a) => a.indexOf(x) == i).length;

				this.selectedCurrency = initialCurrencytoProcess;

				this.getApprovedRequestFunds(initialCurrencytoProcess);
				this.getActiveConnections(initialCurrencytoProcess);
				this.getSuppliersActiveConnections(initialCurrencytoProcess);
				this.getMonthlyAutomaticPayments(initialCurrencytoProcess);
				this.getCurrentFundingRequestsAmount(initialCurrencytoProcess);
				this.getNotPaidDocumentsSum(initialCurrencytoProcess);
				this.getPendingApprovalDocumentsAmount(initialCurrencytoProcess);
			}

			// Set loading states to false after all calculations are complete
			this.loadingContractedAmount = false;
			this.loadingContractsAvailableAmount = false;
			this.loadingBuyersWithActiveContractsCompaniesCount = false;
		});
	}

	private refreshContractDataForCurrency(currency: string) {
		// Set loading states for values that are calculated in this method
		this.loadingContractedAmount = true;
		this.loadingContractsAvailableAmount = true;
		this.loadingBuyersWithActiveContractsCompaniesCount = true;

		let servicesCallsToMake: Array<Observable<OperationResult<ConfirmingContract[]>>> = [];

		this.loggedUser?.Companies.forEach((company) => {
			servicesCallsToMake.push(
				this.serverApiService.getConfirmingContractsForFinancier(<GetFinancierConfirmingContractsInput>{
					FinancierId: company.Id!,
					Currency: currency,
				})
			);
		});

		const getAllRequests = forkJoin(servicesCallsToMake);

		getAllRequests.subscribe((opResults) => {
			for (let index = 0; index < servicesCallsToMake.length; index++) {
				let getConfirmingContracts = opResults[index] as OperationResult<ConfirmingContract[]>;

				if (!getConfirmingContracts.IsValid) {
					let errors = getConfirmingContracts.Errors;
					if (errors && errors.length > 0) {
						this.toasterService.showError(this.translateService.translate('VALIDATION_FAILED', 'list-errorCodes'), {
							errors: errors,
							autoClose: false,
						});
					}
				} else {
					if (!!getConfirmingContracts.Data && getConfirmingContracts.Data.length > 0) {
						this.registeredBuyerCompanies += getConfirmingContracts.Data.length;

						getConfirmingContracts.Data.forEach((contract) => {
							this.contractedAmount += contract.ContractedAmount;
							this.contractsAvailableAmount += contract.AvailableAmount;
							this.contractsAvailableAmountRatio =
								this.contractedAmount > 0 ? 100.0 * (this.contractsAvailableAmount / this.contractedAmount) : 0;
						});

						this.buyersWithActiveContractsCompaniesCount = getConfirmingContracts.Data.filter(
							(contract) => contract.ContractState == 'Active'
						)
							.map((c) => c.Buyer.Id)
							.filter((x, i, a) => a.indexOf(x) == i).length;
					}
				}
			}

			// Set loading states to false after all calculations are complete
			this.loadingContractedAmount = false;
			this.loadingContractsAvailableAmount = false;
			this.loadingBuyersWithActiveContractsCompaniesCount = false;
		});
	}

	private getApprovedRequestFunds(currency: string) {
		this.loadingApprovedRequestFunds = true;
		this.approvedRequestFunds = 0;
		const fundingRequestDocumentStates = ['Approved'];

		this.reportingApiService.getConfirmingFundingRequestsTotalAdvancedAmount(fundingRequestDocumentStates, currency).subscribe({
			next: (opResult: OperationResult<number>) => {
				this.loadingApprovedRequestFunds = false;
				opResult.IsValid
					? (this.approvedRequestFunds = opResult.Data)
					: this.logger.error('[DashboardFinancierComponent] Invalid result from getFundingRequestsTotalAdvancedAmount', opResult);
			},
			error: (httpErrorResponse: HttpErrorResponse) => {
				this.loadingApprovedRequestFunds = false;
				this.logger.error(
					'[DashboardFinancierComponent] HttpError result from getFundingRequestsTotalAdvancedAmount',
					this.domSanitizer.sanitize(SecurityContext.HTML, httpErrorResponse)
				);
			},
		});
	}

	private getActiveConnections(currency: string) {
		this.loadingSuppliersWithActiveConnectionsCompaniesCount = true;
		this.suppliersWithActiveConnectionsCompaniesCount = 0;
		const criteria: ConfirmingConnectionsSearchCriteria = new ConfirmingConnectionsSearchCriteria();
		criteria.PageNumber = 1;
		criteria.RowsPerPage = 2147483647;
		criteria.RestrictionCriteria = new ConfirmingConnectionsSearchCriteria();
		criteria.RestrictionCriteria.ConnectionStateList = ['Active'];
		criteria.RestrictionCriteria.ContractStateList = ['Active'];
		criteria.RestrictionCriteria.Currency = currency;

		this.serverApiService.searchConfirmingConnections(criteria).subscribe({
			next: (operationResult: OperationResult<ConfirmingConnection[]>) => {
				this.loadingSuppliersWithActiveConnectionsCompaniesCount = false;
				if (operationResult.IsValid) {
					if (!!operationResult.Data) {
						if (operationResult.Data.length > 0)
							this.suppliersWithActiveConnectionsCompaniesCount = operationResult.Data.map((c) => c.Supplier.Id).filter(
								(x, i, a) => a.indexOf(x) == i
							).length;
						else this.suppliersWithActiveConnectionsCompaniesCount = 0;
					}
				} else {
					this.toasterService.showError(this.translateService.translate('ERROR_GET_COMPANY_CONTRACTS', 'list-errorCodes'));
				}
			},
			error: (httpErrorResponse: HttpErrorResponse) => {
				this.loadingSuppliersWithActiveConnectionsCompaniesCount = false;
				console.error('error: ' + httpErrorResponse.message);
				this.toasterService.showErrorFromHttpResponse(httpErrorResponse);
			},
		});
	}

	public clickedCurrency(currency: string): void {
		this.resetValues();

		this.selectedCurrency = currency;

		this.orderedCurrencies.forEach((orderedCurrency) => {
			orderedCurrency.Selected = orderedCurrency.Currency == currency;
		});

		this.refreshContractDataForCurrency(currency);
		this.getApprovedRequestFunds(currency);
		this.getActiveConnections(currency);
		this.getSuppliersActiveConnections(currency);
		this.getMonthlyAutomaticPayments(currency);
		this.getCurrentFundingRequestsAmount(currency);
		this.getNotPaidDocumentsSum(currency);
		this.getPendingApprovalDocumentsAmount(currency);
	}

	private resetValues() {
		this.contractedAmount = 0;
		this.contractsAvailableAmount = 0;
		this.contractsAvailableAmountRatio = 0;
		this.approvedRequestFunds = 0;
		this.currentFundingRequestsAmount = 0;
		this.monthlyAutomaticPayments = 0;
		this.buyersWithActiveContractsCompaniesCount = 0;
		this.suppliersWithActiveConnectionsCompaniesCount = 0;
		this.suppliersActiveConnections = 0;
		this.notPaidDocuments = 0;
	}

	public notMultipleCurrencies(currencies: CurrencyViewModel[]): boolean {
		return currencies?.length <= 1;
	}

	public clickedConfirmingTab(): void {
		this.confirmingTabSelected = true;
		this.factoringTabSelected = false;

		this.getContracts();
	}

	private getNotPaidDocumentsSum(currency: string): void {
		this.loadingNotPaidDocuments = true;
		this.notPaidDocuments = 0;
		this.reportingApiService.getNotPaidDocumentsSum(currency).subscribe({
			next: (opResult: OperationResult<number>) => {
				this.loadingNotPaidDocuments = false;
				opResult.IsValid
					? (this.notPaidDocuments = opResult.Data)
					: this.logger.error('[DashboardFinancierComponent] Invalid result from getNotPaidDocumentsSum()', opResult);
			},
			error: (httpErrorResponse: HttpErrorResponse) => {
				this.loadingNotPaidDocuments = false;
				this.logger.error(
					'[DashboardFinacierComponent] Invalid result from getNotPaidDocumentsSum()',
					this.domSanitizer.sanitize(SecurityContext.HTML, httpErrorResponse)
				);
			},
		});
	}

	public clickedFactoringTab(): void {
		this.confirmingTabSelected = false;
		this.factoringTabSelected = true;
		this.selectedCurrency = CurrencyCode.Euro;

		this.getFactoringApprovedCreditAssignments(CurrencyCode.Euro);
		this.getFactoringCurrentCreditAssignmentsAmount(CurrencyCode.Euro);
		this.getFactoringApprovedAdvances(CurrencyCode.Euro);
		this.getFactoringSuppliersWithActiveContractsCompaniesCount();
		this.getFactoringBuyersWithActiveConnectionsCompaniesCount();
		this.getFactoringBuyersActiveConnections();
		this.getPendingApprovalDocumentsAmount(CurrencyCode.Euro);
	}

	private getFactoringApprovedCreditAssignments(currency: string): void {
		this.loadingFactoringApprovedCreditAssignments = true;
		this.factoringApprovedCreditAssignments = 0;
		const creditAssignmentsDocumentStates = ['Approved'];

		this.reportingApiService.getFactoringApprovedCreditAssignments(creditAssignmentsDocumentStates, currency).subscribe({
			next: (opResult: OperationResult<number>) => {
				this.loadingFactoringApprovedCreditAssignments = false;
				opResult.IsValid
					? (this.factoringApprovedCreditAssignments = opResult.Data)
					: this.logger.error('[DashboardFinancierComponent] Invalid result from getFactoringApprovedCreditAssignments', opResult);
			},
			error: (httpErrorResponse: HttpErrorResponse) => {
				this.loadingFactoringApprovedCreditAssignments = false;
				this.logger.error(
					'[DashboardFinancierComponent] HttpError result from getFactoringApprovedCreditAssignments',
					this.domSanitizer.sanitize(SecurityContext.HTML, httpErrorResponse)
				);
			},
		});
	}

	private getFactoringCurrentCreditAssignmentsAmount(currency: string): void {
		this.loadingFactoringCurrentCreditAssignmentsAmount = true;
		this.factoringCurrentCreditAssignmentsAmount = 0;
		this.reportingApiService.getFactoringCurrentCreditAssignmentsAmount(currency).subscribe({
			next: (opResult: OperationResult<number>) => {
				this.loadingFactoringCurrentCreditAssignmentsAmount = false;
				opResult.IsValid
					? (this.factoringCurrentCreditAssignmentsAmount = opResult.Data)
					: this.logger.error('[DashboardFinancierComponent] Invalid result from getFactoringCurrentCreditAssignmentsAmount()', opResult);
			},
			error: (httpErrorResponse: HttpErrorResponse) => {
				this.loadingFactoringCurrentCreditAssignmentsAmount = false;
				this.logger.error(
					'[DashboardFinancierComponent] Invalid result from getFactoringCurrentCreditAssignmentsAmount()',
					this.domSanitizer.sanitize(SecurityContext.HTML, httpErrorResponse)
				);
			},
		});
	}

	private getFactoringApprovedAdvances(currency: string): void {
		this.loadingFactoringApprovedAdvances = true;
		this.factoringApprovedAdvances = 0;
		const advancesStates = ['Approved'];

		this.reportingApiService.getFactoringApprovedAdvances(advancesStates, currency).subscribe({
			next: (opResult: OperationResult<number>) => {
				this.loadingFactoringApprovedAdvances = false;
				opResult.IsValid
					? (this.factoringApprovedAdvances = opResult.Data)
					: this.logger.error('[DashboardFinancierComponent] Invalid result from getFactoringApprovedAdvances', opResult);
			},
			error: (httpErrorResponse: HttpErrorResponse) => {
				this.loadingFactoringApprovedAdvances = false;
				this.logger.error(
					'[DashboardFinancierComponent] HttpError result from getFactoringApprovedAdvances',
					this.domSanitizer.sanitize(SecurityContext.HTML, httpErrorResponse)
				);
			},
		});
	}

	private getFactoringSuppliersWithActiveContractsCompaniesCount(): void {
		this.loadingFactoringContractedAmount = true;
		this.loadingFactoringContractAdvanceableAmount = true;
		this.loadingFactoringContractDocumentPortfolioAmount = true;
		this.loadingFactoringSuppliersWithActiveContractsCompaniesCount = true;

		// Reset values to prevent incrementing on refresh
		this.factoringContractedAmount = 0;
		this.factoringContractAdvanceableAmount = 0;
		this.factoringContractDocumentPortfolioAmount = 0;
		this.factoringSuppliersWithActiveContractsCompaniesCount = 0;

		const criteria: FactoringContractsSearchCriteria = new FactoringContractsSearchCriteria();
		criteria.PageNumber = 1;
		criteria.OrderDescending = true;
		criteria.RowsPerPage = 2147483647;
		criteria.RestrictionCriteria = new FactoringContractsSearchCriteria();
		criteria.RestrictionCriteria.ContractStateList = ['Active'];

		this.serverApiService.searchFactoringContracts(criteria).subscribe((getFactoringContracts) => {
			this.loadingFactoringContractedAmount = false;
			this.loadingFactoringContractAdvanceableAmount = false;
			this.loadingFactoringContractDocumentPortfolioAmount = false;
			this.loadingFactoringSuppliersWithActiveContractsCompaniesCount = false;

			if (!getFactoringContracts.IsValid) {
				let errors = getFactoringContracts.Errors;
				if (errors && errors.length > 0) {
					this.toasterService.showError(this.translateService.translate('VALIDATION_FAILED', 'list-errorCodes'), {
						errors: errors,
						autoClose: false,
					});
				}
			} else {
				if (!!getFactoringContracts.Data && getFactoringContracts.Data.length > 0) {
					getFactoringContracts.Data.forEach((contract) => {
						this.factoringContractedAmount += contract.ContractAmount;
						this.factoringContractAdvanceableAmount += contract.AdvanceableAmount;
						this.factoringContractDocumentPortfolioAmount += contract.DocumentPortfolioAmount;
					});

					this.factoringSuppliersWithActiveContractsCompaniesCount = getFactoringContracts.Data.map((c) => c.Supplier.Id).filter(
						(x, i, a) => a.indexOf(x) == i
					).length;
				}
			}
		});
	}

	private getFactoringBuyersWithActiveConnectionsCompaniesCount() {
		this.loadingFactoringBuyersWithActiveConnectionsCompaniesCount = true;
		this.factoringBuyersWithActiveConnectionsCompaniesCount = 0;
		const criteria: FactoringConnectionsSearchCriteria = new FactoringConnectionsSearchCriteria();
		criteria.PageNumber = 1;
		criteria.RowsPerPage = 2147483647;
		criteria.RestrictionCriteria = new FactoringConnectionsSearchCriteria();
		criteria.RestrictionCriteria.ConnectionStateList = ['Active'];
		criteria.RestrictionCriteria.ContractStateList = ['Active'];

		this.serverApiService.searchFactoringConnections(criteria).subscribe({
			next: (operationResult: OperationResult<FactoringConnection[]>) => {
				this.loadingFactoringBuyersWithActiveConnectionsCompaniesCount = false;
				if (operationResult.IsValid) {
					if (!!operationResult.Data && operationResult.Data.length > 0) {
						this.factoringBuyersWithActiveConnectionsCompaniesCount = operationResult.Data.map((c) => c.Buyer.Id).filter(
							(x, i, a) => a.indexOf(x) == i
						).length;
					}
				} else {
					this.toasterService.showError(this.translateService.translate('ERROR_GET_COMPANY_CONTRACTS', 'list-errorCodes'));
				}
			},
			error: (httpErrorResponse: HttpErrorResponse) => {
				this.loadingFactoringBuyersWithActiveConnectionsCompaniesCount = false;
				console.error('error: ' + httpErrorResponse.message);
				this.toasterService.showErrorFromHttpResponse(httpErrorResponse);
			},
		});
	}

	private getFactoringBuyersActiveConnections() {
		this.loadingFactoringBuyersActiveConnections = true;
		this.factoringBuyersActiveConnections = 0;
		this.reportingApiService.getFactoringBuyersActiveConnections().subscribe({
			next: (opResult: OperationResult<number>) => {
				this.loadingFactoringBuyersActiveConnections = false;
				opResult.IsValid
					? (this.factoringBuyersActiveConnections = opResult.Data)
					: this.logger.error('[DashboardFinancierComponent] Invalid result from getFactoringBuyersActiveConnections()', opResult);
			},
			error: (httpErrorResponse: HttpErrorResponse) => {
				this.loadingFactoringBuyersActiveConnections = false;
				this.logger.error(
					'[DashboardFinancierComponent] Invalid result from getFactoringBuyersActiveConnections()',
					this.domSanitizer.sanitize(SecurityContext.HTML, httpErrorResponse)
				);
			},
		});
	}

	public navigateToFilesIntegration(key: string): void {
		if (!this.loggedUser?.IsFinancier) {
			SearchFilesIntegrationComponent.navigateWithFileStatus(this.router, this.fileIntegrationStateService, key);
		}
	}
}
